import { Grid } from "@material-ui/core";
import { useEffect, useState, useRef } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { Switch, Route, Redirect } from "react-router-dom";

// Components
import MainContainer from "./MainContainer";
import Login from "./authentication/login/Login";
import SignUp from "./authentication/signUp/SignUp";
import ForgotPassword from "./authentication/forgotPassword/ForgotPassword";
import ResetPassword from "./authentication/forgotPassword/ResetPassword";
import SessionValidationPopover from "../elements/SessionValidationPopover";

// Styles
import QuestionnaireStyle from "../../assets/styles/materialUI/questionnaire/QuestionnaireStyle";

// Services
import auth from "../../services/auth/auth";
import sessionStorage from "../../services/storage/SessionStorage";

// Connectors
import backendConnector from "../../services/connectors/backendConnector";

// Providers
import { useUserInfo, useUserInfoUpdate } from "../../contexts/userInfoContext";

const Questionnaire = () => {
	const updateUser = useUserInfoUpdate();

	const classes = QuestionnaireStyle();
	const userInfo = useUserInfo();
	const [showSessionExpiration, setShowSessionExpiration] = useState(false);
	const [sessionExpirationCounter, setSessionExpirationCounter] = useState(30);
	const showingRef = useRef(false);
	const inactivityTimerRef = useRef(null);
	const countdownIntervalRef = useRef(null);
	const resetTimerFunctionRef = useRef(null);

	const clearInactivityTimeout = () => {
		if (inactivityTimerRef.current) {
			clearTimeout(inactivityTimerRef.current);
			inactivityTimerRef.current = null;
		}
	};

	const clearCountdownInterval = () => {
		if (countdownIntervalRef.current) {
			clearInterval(countdownIntervalRef.current);
			countdownIntervalRef.current = null;
		}
	};

	const handleLogout = () => {
		clearInactivityTimeout();
		clearCountdownInterval();
		showingRef.current = false;
		setShowSessionExpiration(false);
		setSessionExpirationCounter(30);
		sessionStorage.clear();
		updateUser(null);
	};

	const extendSession = async () => {
		let data = {
			refresh_key: sessionStorage.getAuthObj()?.refresh_token,
		};
		let response = await backendConnector.extendSession(data);
		if (response?.access_token) {
			let token = auth.setTokens(response);
			updateUser(token);
			clearCountdownInterval();
			setShowSessionExpiration(false);
			setSessionExpirationCounter(30);
			startInactivityTimer();
		} else {
			handleLogout();
		}
	};

	useEffect(() => {
		if (!userInfo) return;

		let parsed = typeof userInfo === "string" ? JSON.parse(userInfo) : userInfo;
		let id = setInterval(
			() => {
				if (!parsed?.access_expires_at) return;
				const expirationTime = new Date(parsed.access_expires_at + "Z").getTime();
				const now = Date.now();

				if (now >= expirationTime) {
					sessionStorage.clear();
					updateUser(null);
					clearInterval(id);
					return;
				}

				const bufferTime = expirationTime - 10 * 60 * 1000;
				if (now >= bufferTime) {
					extendSession();
					clearInterval(id);
				}
			},
			5 * 60 * 1000,
		);

		return () => clearInterval(id);
	}, [userInfo]);

	const startInactivityTimer = () => {
		clearInactivityTimeout();
		clearCountdownInterval();
		setSessionExpirationCounter(30);
		inactivityTimerRef.current = setTimeout(
			() => {
				countdownIntervalRef.current = setInterval(() => {
					setSessionExpirationCounter((prevSeconds) => {
						if (prevSeconds <= 1) {
							handleLogout();
							return 0;
						}
						return prevSeconds - 1;
					});
				}, 1000);
				showingRef.current = true;
				setShowSessionExpiration(true);
			},
			5 * 60 * 1000,
		);
	};

	const handleContinue = () => {
		clearCountdownInterval();
		showingRef.current = false;
		setShowSessionExpiration(false);
		setSessionExpirationCounter(30);
		extendSession();
	};

	useEffect(() => {
		if (!userInfo) return;

		const resetTimer = () => {
			if (showingRef.current) {
				return;
			}
			startInactivityTimer();
		};

		resetTimerFunctionRef.current = resetTimer;
		window.addEventListener("mousemove", resetTimer);
		window.addEventListener("keypress", resetTimer);
		window.addEventListener("click", resetTimer);
		window.addEventListener("scroll", resetTimer);

		startInactivityTimer();

		return () => {
			clearInactivityTimeout();
			clearCountdownInterval();
			window.removeEventListener("mousemove", resetTimer);
			window.removeEventListener("keypress", resetTimer);
			window.removeEventListener("click", resetTimer);
			window.removeEventListener("scroll", resetTimer);
		};
	}, [userInfo]);

	if (!userInfo) {
		return (
			<Switch>
				<Route path="/questionnaire/login" component={Login} />
				<Route path="/questionnaire/signup" component={SignUp} />
				<Route path="/questionnaire/forgot_password" component={ForgotPassword} />
				<Route path="/questionnaire/reset_password/:token" component={ResetPassword} />
				<Route path="*">
					<Redirect to="/questionnaire/login" />
				</Route>
			</Switch>
		);
	}

	if (userInfo) {
		return (
			<Router>
				<Grid className={classes.root}>
					<Grid className={classes.content}>
						<MainContainer />
					</Grid>
					<SessionValidationPopover
						sessionExpirationCounter={sessionExpirationCounter}
						showSessionExpiration={showSessionExpiration}
						onContinue={handleContinue}
						onLogout={handleLogout}
					/>
				</Grid>
			</Router>
		);
	}
};

export default Questionnaire;
